import { Link } from 'react-router-dom';
import LegalPage, { Section } from './components/LegalPage';
import { CONTACT_EMAIL } from './constants';

function Code({ children }) {
  return (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.8em] text-gray-800 dark:bg-gray-800 dark:text-gray-200">
      {children}
    </code>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs leading-relaxed text-gray-800 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-200">
      <code>{children}</code>
    </pre>
  );
}

function Docs() {
  // The MCP endpoint is this app's own origin plus /mcp — the edge proxy routes
  // it to the API — so the instructions always name the host you are reading
  // them on.
  const mcpUrl = `${window.location.origin}/mcp`;

  const remoteConfig = `{
  "mcpServers": {
    "dissona": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${mcpUrl}"]
    }
  }
}`;

  return (
    <LegalPage title="Docs">
      <Section title="Connect your AI agent">
        <p>
          Dissona speaks the{' '}
          <a
            href="https://modelcontextprotocol.io"
            target="_blank"
            rel="noreferrer noopener"
            className="link hover:underline"
          >
            Model Context Protocol
          </a>
          , so an agent you already use — Claude Code, Claude Desktop, Cursor, Windsurf, ChatGPT —
          can work directly with your library. Ask it to research a topic and file the result as a
          project, upload a PDF, start processing, or hand you back a link to a finished chapter,
          without leaving the chat.
        </p>
        <p>
          Everything runs over one endpoint: <Code>{mcpUrl}</Code>
        </p>
      </Section>

      <Section title="Before you start">
        <p>
          You need a Dissona account, and — for agents that do not yet speak remote MCP natively —
          Node.js 18 or newer, since the connection helper <Code>mcp-remote</Code> is fetched on
          demand with <Code>npx</Code>. Check with <Code>node --version</Code>.
        </p>
        <p>
          There is no token to copy and nothing Dissona-specific to install. The first connection
          opens a browser tab where you sign in and approve the agent; your agent stores the
          resulting token itself.
        </p>
      </Section>

      <Section title="Claude Code">
        <p>Add the server from your terminal:</p>
        <CodeBlock>{`claude mcp add --transport http dissona ${mcpUrl}`}</CodeBlock>
        <p>
          Then run <Code>/mcp</Code> inside Claude Code and pick <strong>dissona</strong> to
          authenticate. Add <Code>--scope user</Code> to the command if you want the server
          available in every project rather than just the current one.
        </p>
      </Section>

      <Section title="Claude Desktop">
        <p>
          Recent versions connect without any helper: open{' '}
          <strong>Settings → Connectors → Add custom connector</strong> and paste the endpoint above.
          Claude opens the login and consent screens for you.
        </p>
        <p>
          On an older build, edit <Code>claude_desktop_config.json</Code> instead — it lives at{' '}
          <Code>~/Library/Application Support/Claude/</Code> on macOS,{' '}
          <Code>%APPDATA%\Claude\</Code> on Windows and <Code>~/.config/Claude/</Code> on Linux — and
          add:
        </p>
        <CodeBlock>{remoteConfig}</CodeBlock>
        <p>Quit Claude Desktop completely and start it again; config is read only at launch.</p>
      </Section>

      <Section title="Cursor, Windsurf and other MCP hosts">
        <p>
          Any host that takes the standard <Code>mcpServers</Code> shape uses exactly the block
          above. Only the file differs: <Code>~/.cursor/mcp.json</Code> for Cursor,{' '}
          <Code>~/.codeium/windsurf/mcp_config.json</Code> for Windsurf. Hosts that support remote
          MCP servers directly can skip <Code>mcp-remote</Code> and take the URL as it is.
        </p>
        <p>
          ChatGPT connects the same way where custom connectors are available on your plan: add a
          connector pointing at the endpoint above and complete the sign-in it prompts for.
        </p>
      </Section>

      <Section title="Signing in the first time">
        <p>
          The first time your agent loads the server, a browser tab opens on Dissona. Sign in with
          your username and password or with Google, then approve the consent screen — it names the
          agent asking for access. Close the tab when it tells you to; the agent is connected.
        </p>
        <p>
          Each agent holds its own token and refreshes it in the background, so you will not be asked
          again for a long while. Connecting a second agent does not disturb the first.
        </p>
      </Section>

      <Section title="What to ask it">
        <p>Once connected, try:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>&ldquo;List my Dissona projects.&rdquo;</li>
          <li>&ldquo;What&rsquo;s the progress on the book I uploaded yesterday?&rdquo;</li>
          <li>&ldquo;Upload this PDF and start processing it once extraction finishes.&rdquo;</li>
          <li>&ldquo;Research X, write it up, and put it in my library as a new project.&rdquo;</li>
          <li>&ldquo;Show me the available voices and change my default.&rdquo;</li>
          <li>&ldquo;Give me the audio link for chapter 3.&rdquo;</li>
        </ul>
        <p>
          The agent can read your library, add to it, trigger processing and change your settings. It
          cannot delete your projects, change your password, or reach anyone else&rsquo;s data.
        </p>
        <p>
          One deliberate limitation: an agent hands you a link to audio, it never plays it. Open the
          link in your browser, in Dissona, or in the podcast app subscribed to that project&rsquo;s
          feed.
        </p>
      </Section>

      <Section title="Disconnecting an agent">
        <p>
          Remove the <Code>dissona</Code> entry from that agent&rsquo;s config — or run{' '}
          <Code>claude mcp remove dissona</Code> in Claude Code — and delete its cached token with{' '}
          <Code>rm -rf ~/.mcp-auth/*dissona*</Code>. Your other agents keep working; each holds a
          separate token.
        </p>
      </Section>

      <Section title="If something does not work">
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">
            No browser tab opens.
          </strong>{' '}
          Check that Node.js 18+ is installed and that your shell finds <Code>npx</Code>, then
          restart the agent.
        </p>
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">
            &ldquo;Invalid client&rdquo; or a redirect-URI mismatch.
          </strong>{' '}
          Delete <Code>~/.mcp-auth</Code> and restart, so the helper registers from scratch.
        </p>
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">
            The tools never appear.
          </strong>{' '}
          Validate the JSON — a trailing comma is the usual culprit — and make sure you fully quit
          and relaunched the agent.
        </p>
        <p>
          Still stuck? Write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="link hover:underline">
            {CONTACT_EMAIL}
          </a>
          , or see the <Link to="/contact" className="link hover:underline">Contact</Link>{' '}
          page.
        </p>
      </Section>
    </LegalPage>
  );
}

export default Docs;
